// Stub catalogue + user progress for the achievements page. Will be replaced
// with `/api/achievements` once the backend lands. Categories are intentionally
// few — better to grow the list over time than dump 50 medals at launch.

export type AchievementCategoryCode =
  | 'STREAK'
  | 'VOCABULARY'
  | 'PRACTICE'
  | 'EXPLORATION';

export interface AchievementCategory {
  code: AchievementCategoryCode;
  title: string;
  description: string;
}

export interface AchievementEntry {
  code: string;
  category: AchievementCategoryCode;
  title: string;
  description: string;
  threshold: number;
  current: number;
  unlocked: boolean;
  unlockedAt?: string; // YYYY-MM-DD
  unit?: string; // for the progress label, e.g. "слів", "днів"
}

export const ACHIEVEMENT_CATEGORIES: AchievementCategory[] = [
  {
    code: 'STREAK',
    title: 'Серії днів',
    description: 'Постійність важливіша за обсяг — нагороди за регулярність.',
  },
  {
    code: 'VOCABULARY',
    title: 'Словник',
    description: 'Зростання активного словникового запасу.',
  },
  {
    code: 'PRACTICE',
    title: 'Практика',
    description: 'Сесії вправ, тести, час за вправами.',
  },
  {
    code: 'EXPLORATION',
    title: 'Дослідження',
    description: 'Робота з реальними текстами в «В дії».',
  },
];

export const ACHIEVEMENTS: AchievementEntry[] = [
  // Streaks
  {
    code: 'STREAK_3',
    category: 'STREAK',
    title: 'Три дні',
    description: '3 дні поспіль активності.',
    threshold: 3,
    current: 3,
    unlocked: true,
    unlockedAt: '2026-06-05',
    unit: 'днів',
  },
  {
    code: 'STREAK_7',
    category: 'STREAK',
    title: 'Тиждень поспіль',
    description: '7 днів поспіль без перерви.',
    threshold: 7,
    current: 7,
    unlocked: true,
    unlockedAt: '2026-06-09',
    unit: 'днів',
  },
  {
    code: 'STREAK_30',
    category: 'STREAK',
    title: 'Місяць звички',
    description: '30 днів поспіль — звичка точно сформована.',
    threshold: 30,
    current: 7,
    unlocked: false,
    unit: 'днів',
  },
  {
    code: 'STREAK_100',
    category: 'STREAK',
    title: 'Сотня днів',
    description: '100 днів поспіль активності.',
    threshold: 100,
    current: 7,
    unlocked: false,
    unit: 'днів',
  },
  {
    code: 'STREAK_365',
    category: 'STREAK',
    title: 'Рік поспіль',
    description: '365 днів поспіль — рівень майстра.',
    threshold: 365,
    current: 7,
    unlocked: false,
    unit: 'днів',
  },

  // Vocabulary
  {
    code: 'WORDS_10',
    category: 'VOCABULARY',
    title: 'Перші 10 слів',
    description: '10 слів у твоєму активному словнику.',
    threshold: 10,
    current: 10,
    unlocked: true,
    unlockedAt: '2026-05-04',
    unit: 'слів',
  },
  {
    code: 'WORDS_100',
    category: 'VOCABULARY',
    title: 'Сто слів',
    description: '100 слів у твоєму активному словнику.',
    threshold: 100,
    current: 100,
    unlocked: true,
    unlockedAt: '2026-05-22',
    unit: 'слів',
  },
  {
    code: 'WORDS_500',
    category: 'VOCABULARY',
    title: '500 вивчених слів',
    description: 'Доведи активний словник до 500 слів.',
    threshold: 500,
    current: 412,
    unlocked: false,
    unit: 'слів',
  },
  {
    code: 'WORDS_1000',
    category: 'VOCABULARY',
    title: 'Тисяча',
    description: '1000 слів — рівень B2/C1.',
    threshold: 1000,
    current: 412,
    unlocked: false,
    unit: 'слів',
  },
  {
    code: 'VERBS_50',
    category: 'VOCABULARY',
    title: 'Неправильні дієслова',
    description: 'Вивчити 50 неправильних дієслів.',
    threshold: 50,
    current: 22,
    unlocked: false,
    unit: 'дієслів',
  },

  // Practice
  {
    code: 'FIRST_SESSION',
    category: 'PRACTICE',
    title: 'Перша сесія',
    description: 'Завершити першу сесію вправ.',
    threshold: 1,
    current: 1,
    unlocked: true,
    unlockedAt: '2026-05-02',
  },
  {
    code: 'SESSIONS_10',
    category: 'PRACTICE',
    title: '10 сесій',
    description: 'Завершити 10 сесій вправ.',
    threshold: 10,
    current: 10,
    unlocked: true,
    unlockedAt: '2026-05-18',
    unit: 'сесій',
  },
  {
    code: 'SESSIONS_50',
    category: 'PRACTICE',
    title: '50 сесій',
    description: 'Завершити 50 сесій вправ.',
    threshold: 50,
    current: 27,
    unlocked: false,
    unit: 'сесій',
  },
  {
    code: 'FIRST_TEST',
    category: 'PRACTICE',
    title: 'Перший тест',
    description: 'Пройти перший тест словникового запасу.',
    threshold: 1,
    current: 1,
    unlocked: true,
    unlockedAt: '2026-05-12',
  },
  {
    code: 'TESTS_5',
    category: 'PRACTICE',
    title: 'П’ять тестів',
    description: 'Пройти 5 тестів словникового запасу.',
    threshold: 5,
    current: 2,
    unlocked: false,
    unit: 'тестів',
  },
  {
    code: 'PRACTICE_HOURS_10',
    category: 'PRACTICE',
    title: '10 годин практики',
    description: 'Загалом 10 годин у вправах.',
    threshold: 600,
    current: 142,
    unlocked: false,
    unit: 'хв',
  },

  // Exploration (In Action)
  {
    code: 'IN_ACTION_FIRST',
    category: 'EXPLORATION',
    title: 'Перший текст',
    description: 'Розібрати перший текст у «В дії».',
    threshold: 1,
    current: 1,
    unlocked: true,
    unlockedAt: '2026-05-25',
  },
  {
    code: 'IN_ACTION_10',
    category: 'EXPLORATION',
    title: '10 текстів',
    description: 'Розібрати 10 текстів у «В дії».',
    threshold: 10,
    current: 4,
    unlocked: false,
    unit: 'текстів',
  },
  {
    code: 'COMEBACK_30',
    category: 'EXPLORATION',
    title: 'Повернення',
    description: 'Повернутися після 30 днів перерви.',
    threshold: 1,
    current: 0,
    unlocked: false,
  },
];
